// Reading a demo folder: what the manifest says, and how a session in it is identified.
import type { ClankPermissions } from "../../api/types";

/** One session's entry in the manifest. */
export type ManifestRecording = {
  file: string;
  owner?: string;
  name?: string;
  include_in_processes?: boolean;
};

export type ManifestUser = {
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  /** Defaults server-side to everything `OnlyMine`, not an admin — what a demo persona should see. */
  permissions?: ClankPermissions | null;
};

export type DemoManifest = {
  company: { id: number; name: string };
  users: ManifestUser[];
  recordings: ManifestRecording[];
};

export const MANIFEST_NAME = "manifest.json";

/** Demo companies live in their own band so they never collide with a real tenant. */
export const DEMO_COMPANY_ID_MIN = 9000;
export const DEMO_COMPANY_ID_MAX = 9099;

/**
 * The server's identity for an imported session: `(user_id, relative_path_on_disk)`, where the path
 * is the start time formatted as `fake_%Y-%m-%d_%H_%M_%S` in UTC. Recomputing it here is what lets
 * a re-import recognise a session it already created and update it instead of colliding with it.
 */
export const fakeRecordingPath = (startTimestamp: number): string => {
  const at = new Date(startTimestamp * 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return [
    `fake_${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`,
    pad(at.getUTCHours()),
    pad(at.getUTCMinutes()),
    pad(at.getUTCSeconds()),
  ].join("_");
};

export const sessionKey = (userId: string, startTimestamp: number): string =>
  `${userId}|${fakeRecordingPath(startTimestamp)}`;

/**
 * A password for a demo persona: 20 random characters with every class the server's strength
 * check requires. Generated here because `create_user` takes a password rather than minting one,
 * and shown to the admin exactly once in the import result.
 */
export const generatePassword = (): string => {
  const classes = [
    "abcdefghijkmnpqrstuvwxyz",
    "ABCDEFGHJKLMNPQRSTUVWXYZ",
    "23456789",
    "!@#$%^&*-_=+",
  ];
  const all = classes.join("");
  const bytes = crypto.getRandomValues(new Uint8Array(20));
  const chars = Array.from(bytes, (byte, index) => {
    const pool = index < classes.length ? classes[index] : all;
    return pool[byte % pool.length];
  });
  // The class-guaranteeing characters were placed first; shuffle so they are not.
  for (let index = chars.length - 1; index > 0; index -= 1) {
    const swap = crypto.getRandomValues(new Uint8Array(1))[0] % (index + 1);
    [chars[index], chars[swap]] = [chars[swap], chars[index]];
  }
  return chars.join("");
};

export const parseManifest = (raw: string): DemoManifest => {
  const parsed: unknown = JSON.parse(raw);
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("The manifest must contain a JSON object.");
  }
  const { company, users, recordings } = parsed as Partial<DemoManifest>;
  if (!company || typeof company.id !== "number" || typeof company.name !== "string") {
    throw new Error('The manifest needs a "company" with a numeric "id" and a "name".');
  }
  if (users !== undefined && !Array.isArray(users)) {
    throw new Error('"users" must be an array when present.');
  }
  if (recordings !== undefined && !Array.isArray(recordings)) {
    throw new Error('"recordings" must be an array when present.');
  }
  (users ?? []).forEach((user, index) => {
    if (!user || typeof user.username !== "string" || user.username.trim() === "") {
      throw new Error(`users[${index}] has no "username".`);
    }
  });
  (recordings ?? []).forEach((recording, index) => {
    if (!recording || typeof recording.file !== "string" || recording.file.trim() === "") {
      throw new Error(`recordings[${index}] has no "file".`);
    }
  });

  return { company, users: users ?? [], recordings: recordings ?? [] };
};

/**
 * The start timestamp an analysis file carries, which is what identifies the session.
 *
 * The analysis header is camelCase (`AnalyticsData` is `rename_all = "camelCase"`), while the
 * nodes inside it are not — so `startTimestamp` is the real name. The snake_case spelling is read
 * too, because it is the mistake a generator makes when it copies the node fields' style upward,
 * and failing here would mean uploading the file only for the server to reject it.
 */
export const analysisStartTimestamp = (analysis: unknown): number => {
  if (analysis === null || typeof analysis !== "object") {
    throw new Error("The analysis file must contain a JSON object.");
  }
  const header = analysis as { startTimestamp?: unknown; start_timestamp?: unknown };
  const start = header.startTimestamp ?? header.start_timestamp;
  if (typeof start !== "number" || !Number.isFinite(start)) {
    throw new Error('The analysis file has no numeric "startTimestamp".');
  }
  return start;
};

/**
 * Every file in a drop, folders included. A dropped directory arrives as an entry that has to be
 * walked; a dropped selection of files does not. Both end up as a flat list keyed by basename,
 * because that is what the manifest names.
 */
export const filesFromDrop = async (transfer: DataTransfer): Promise<File[]> => {
  const entries = Array.from(transfer.items)
    .map(item => (item.kind === "file" ? item.webkitGetAsEntry() : null))
    .filter((entry): entry is FileSystemEntry => entry !== null);

  // No entry API (or no folders): the plain file list is already what we need.
  if (entries.length === 0) {
    return Array.from(transfer.files);
  }

  const collected: File[] = [];
  const walk = async (entry: FileSystemEntry): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File | null>(resolve => {
        (entry as FileSystemFileEntry).file(
          resolved => resolve(resolved),
          () => resolve(null),
        );
      });
      if (file) collected.push(file);
      return;
    }
    if (!entry.isDirectory) return;
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      // readEntries returns at most a browser-chosen batch, so it has to be drained.
      const batch = await new Promise<FileSystemEntry[]>(resolve => {
        reader.readEntries(
          resolved => resolve(resolved),
          () => resolve([]),
        );
      });
      if (batch.length === 0) return;
      for (const child of batch) {
        await walk(child);
      }
    }
  };

  for (const entry of entries) {
    await walk(entry);
  }
  return collected;
};
